'use client'

import { useRouter } from 'next/navigation'
import { useState } from 'react'

import { formOptions } from '@tanstack/react-form-nextjs'
import { LoaderCircle } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCountdown } from 'usehooks-ts'

import { useAppForm } from '@/components/form'
import { Button } from '@/components/ui/button'

import {
  useForgotPasswordMutation,
  useResendOtpMutation,
  useResetPasswordMutation,
  useVerifyOtpMutation
} from '@/query/auth'
import { apiFormValidation } from '@/utils'

const forgotPasswordFormOpts = formOptions({
  defaultValues: {
    email: '',
    otp: '',
    password: '',
    password_confirmation: ''
  }
})

export type ForgotPasswordStep = 'complete' | 'email' | 'otp' | 'password'

export default function Page() {
  const t = useTranslations('auth.forgotPassword')
  const tValidation = useTranslations('auth.forgotPassword.validation')
  const router = useRouter()
  const [currentStep, setCurrentStep] = useState<ForgotPasswordStep>('email')
  const [email, setEmail] = useState<null | string>(null)
  const [verifiedOtp, setVerifiedOtp] = useState<null | string>(null)
  const [count, { resetCountdown, startCountdown }] = useCountdown({
    countStart: 300,
    intervalMs: 1000
  })

  const { isPending: isForgotPasswordPending, mutateAsync: forgotPassword } =
    useForgotPasswordMutation()
  const { isPending: isResendOtpPending, mutateAsync: resendOtp } =
    useResendOtpMutation()
  const { isPending: isVerifyOtpPending, mutateAsync: verifyOtp } =
    useVerifyOtpMutation()
  const { isPending: isResetPasswordPending, mutateAsync: resetPassword } =
    useResetPasswordMutation()

  const form = useAppForm({
    ...forgotPasswordFormOpts,
    onSubmit: async ({ formApi, value }) =>
      apiFormValidation(async () => {
        if (currentStep === 'email') {
          await forgotPassword(
            { email: value.email },
            {
              onSuccess: () => {
                setEmail(value.email)
                form.setFieldValue('otp', '')
                setCurrentStep('otp')
                startCountdown()
              }
            }
          )
        } else if (currentStep === 'otp') {
          await verifyOtp(
            { code: value.otp, email: email ?? '' },
            {
              onError: () => {
                form.setFieldValue('otp', '')
                setVerifiedOtp(null)
              },
              onSuccess: () => {
                setVerifiedOtp(value.otp)
                setCurrentStep('password')
              }
            }
          )
        } else if (currentStep === 'password') {
          await resetPassword(
            {
              code: verifiedOtp ?? '',
              email: email ?? '',
              password: value.password,
              password_confirmation: value.password_confirmation
            },
            {
              onSuccess: () => {
                setCurrentStep('complete')
              }
            }
          )
        }
      }, formApi),
    validators: {
      onChange: ({ value }) => {
        const errors = {
          fields: {}
        } as {
          fields: Record<string, string>
        }

        // Only validate the current step
        if (currentStep === 'email' && !value.email) {
          errors.fields.email = tValidation('emailRequired')
        }
        if (currentStep === 'otp' && (!value.otp || value.otp.length !== 6)) {
          errors.fields.otp = tValidation('otpRequired')
        }
        if (currentStep === 'password') {
          if (!value.password) {
            errors.fields.password = tValidation('passwordRequired')
          } else {
            // Password strength validation
            const hasUpperCase = /[A-Z]/.test(value.password)
            const hasLowerCase = /[a-z]/.test(value.password)
            const hasNumber = /[0-9]/.test(value.password)
            const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
              value.password
            )

            if (
              !hasUpperCase ||
              !hasLowerCase ||
              !hasNumber ||
              !hasSpecialChar
            ) {
              errors.fields.password = tValidation('passwordComplexity')
            }
          }
          if (!value.password_confirmation) {
            errors.fields.password_confirmation =
              tValidation('passwordRequired')
          }
          if (
            value.password &&
            value.password_confirmation &&
            value.password !== value.password_confirmation
          ) {
            errors.fields.password_confirmation =
              tValidation('passwordsDontMatch')
          }
        }

        return errors
      }
    }
  })

  const handleStartOver = () => {
    setCurrentStep('email')
    setEmail(null)
    setVerifiedOtp(null)
    form.reset({
      email: '',
      otp: '',
      password: '',
      password_confirmation: ''
    })
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'complete':
        return (
          <div className="flex flex-col items-center gap-5">
            <div className="text-center">
              <h1 className="text-lg font-bold text-green-600">
                {t('completeStep.title')}
              </h1>
              <h3 className="text-muted-foreground text-sm">
                {t('completeStep.description', { email: email ?? '' })}
              </h3>
            </div>
            <button
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-2"
              onClick={() => {
                handleStartOver()
                router.push('/login')
              }}
            >
              {t('completeStep.loginButton')}
            </button>
          </div>
        )

      case 'email':
        return (
          <div className="flex flex-col gap-5">
            <div>
              <h1 className="text-lg font-bold">{t('emailStep.title')}</h1>
              <h3 className="text-muted-foreground text-sm">
                {t('emailStep.description')}
              </h3>
            </div>

            <form.AppField
              children={(field) => (
                <field.Input placeholder={t('emailPlaceholder')} type="email" />
              )}
              key={`email-field-${currentStep}`}
              name="email"
            />

            <form.AppForm>
              <form.SubmitButton isLoading={isForgotPasswordPending}>
                {t('emailStep.submitButton')}
              </form.SubmitButton>
            </form.AppForm>
          </div>
        )

      case 'otp':
        return (
          <div className="flex flex-col items-center gap-5">
            <div>
              <h1 className="text-lg font-bold">{t('otpStep.title')}</h1>
              <h3 className="text-muted-foreground text-sm">
                {t('otpStep.description', { email: email ?? '' })}
              </h3>
            </div>

            <form.AppField
              children={(field) => (
                <field.OTP
                  containerClassName="scale-120"
                  maxLength={6}
                  onComplete={() => {}}
                />
              )}
              key={`otp-field-${currentStep}`}
              name="otp"
            />
            <div className="w-full space-y-2">
              <form.AppForm>
                <form.SubmitButton
                  className="h-10 w-full rounded-3xl"
                  isLoading={isVerifyOtpPending}
                >
                  {t('otpStep.verifyButton')}
                </form.SubmitButton>
              </form.AppForm>

              <Button
                className="h-10 w-full rounded-3xl"
                disabled={count > 0 || isResendOtpPending}
                onClick={async () => {
                  await resendOtp({ email: email ?? '' })
                  form.setFieldValue('otp', '')
                  resetCountdown()
                  startCountdown()
                }}
              >
                {isResendOtpPending && (
                  <LoaderCircle className="animate-spin" size={16} />
                )}
                {t('otpStep.resendButton')}
                {count > 0 && !isResendOtpPending && (
                  <>
                    {' '}
                    {Math.floor(count / 60)
                      .toString()
                      .padStart(2, '0')}
                    :{(count % 60).toString().padStart(2, '0')}
                  </>
                )}
              </Button>
            </div>

            <Button
              className="w-full"
              onClick={handleStartOver}
              variant="secondary"
            >
              {t('otpStep.backToEmail')}
            </Button>
          </div>
        )

      case 'password':
        return (
          <div className="flex flex-col gap-5">
            <div>
              <h1 className="text-lg font-bold">{t('passwordStep.title')}</h1>
              <h3 className="text-muted-foreground text-sm">
                {t('passwordStep.description', { email: email ?? '' })}
              </h3>
            </div>

            <form.AppField
              children={(field) => (
                <field.Password
                  placeholder={t('passwordStep.newPasswordPlaceholder')}
                />
              )}
              key={`password-field-${currentStep}`}
              name="password"
            />
            <form.AppField
              children={(field) => (
                <field.Password
                  placeholder={t('passwordStep.confirmPasswordPlaceholder')}
                />
              )}
              name="password_confirmation"
            />

            <form.AppForm>
              <form.SubmitButton isLoading={isResetPasswordPending}>
                {t('passwordStep.submitButton')}
              </form.SubmitButton>
            </form.AppForm>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <form
      className="w-full max-w-7xl"
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      {renderCurrentStep()}
    </form>
  )
}
